// @ts-strict-ignore
import { Injectable, NgZone, ViewContainerRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LMLoggerService } from '@services/lm-logger.service';
import { catchError, takeUntil } from 'rxjs/operators';
import * as urltemplate from 'url-template';
import { LMFieldPickerComponent } from '@modal/field-picker/lm-field-picker.component';
import { SohoModalDialogService } from 'ids-enterprise-ng';
import { AppConstants, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { Observable } from 'rxjs';
import { Observer, Subject, throwError, timer } from 'rxjs';
import {
  LMFieldOperatorOptionState, LMFieldPickerOperatorOptions,
  LMFieldPickerRequest,
  LMFieldPickerResponse,
  LMFieldPickerSelectOptions,
  LMFieldPickerSelectResponse,
  LMFieldPickerView,
  LMFormatStyleOptionState,
  LMOperatorInfo,
  LMPeriodInfo,
  LMPeriodOperatorInfo,
  LMTypeOperatorInfo,
  LMTypeOperatorOptionState,
  LMYearPeriodInfo
} from '@modal/field-picker/lm-field-picker';
import { LMContextService, LMFormatUtilService } from '@app/services';
import { LMFieldPickerHelpComponent } from "@modal/field-picker/lm-field-picker-help.component";
import { LMFieldPickerOperatorComponent } from "@app/components";

@Injectable()
export class LMFieldPickerService {

  public static ADD_OPERATOR_BUTTON_ID = 'add-period-operator';

  // FIELD OPERATORS ==================================================================================
  public OTM_FIELD_OPERATORS_GROUP: Array<LMFieldOperatorOptionState> = [
    { value: 'first', label: Soho.Locale.translate('FieldPicker_first_field_operator') },
    { value: 'last', label: Soho.Locale.translate('FieldPicker_last_field_operator') },
    {
      label: Soho.Locale.translate('FieldPicker_calculation_field_operator_group'), children: [
        { value: 'instance count of', label: Soho.Locale.translate('FieldPicker_instance_count_field_operator') },
      ]
    },
  ];

  public OTM_BCD_FIELD_OPERATORS_GROUP: Array<LMFieldOperatorOptionState> = [
    { value: 'first', label: Soho.Locale.translate('FieldPicker_first_field_operator') },
    { value: 'last', label:  Soho.Locale.translate('FieldPicker_last_field_operator') },
    {
      label:  Soho.Locale.translate('FieldPicker_calculation_field_operator_group'), children: [
        { value: 'sum', label: Soho.Locale.translate('FieldPicker_sum_field_operator') },
        { value: 'avg', label: Soho.Locale.translate('FieldPicker_avg_field_operator') },
        { value: 'min', label: Soho.Locale.translate('FieldPicker_min_field_operator') },
        { value: 'max', label: Soho.Locale.translate('FieldPicker_max_field_operator') },
        { value: 'instance count of', label: Soho.Locale.translate('FieldPicker_instance_count_field_operator') },
      ]
    },
  ];

  public AUDIT_FIELD_OPERATORS: Array<LMFieldOperatorOptionState> = [
    { value: 'old', label: Soho.Locale.translate('FieldPicker_old_field_operator') },
  ];

  // TYPE OPERATORS ==================================================================================
  public FIELD_INFO_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'size', label: Soho.Locale.translate('FieldPicker_size_type_operator') },
    { value: 'label', label: Soho.Locale.translate('FieldPicker_label_type_operator') },
    { value: 'name', label: Soho.Locale.translate('FieldPicker_name_type_operator') },
    { value: 'full name', label: Soho.Locale.translate('FieldPicker_fullname_type_operator')  },
  ];

  public OTHER_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'as of', label: Soho.Locale.translate('FieldPicker_asofdate_type_operator') },
  ];

  public STRING_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'uppercase', label: Soho.Locale.translate('FieldPicker_uppercase_type_operator')  },
    { value: 'lowercase', label: Soho.Locale.translate('FieldPicker_lowercase_type_operator') },
  ];

  public TIME_STAMP_FORMATTING_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'timestamp format', label: Soho.Locale.translate('FieldPicker_timestampFormat_type_operator')  },
    { value: 'date format', label: Soho.Locale.translate('FieldPicker_dateFormat_type_operator') },
    { value: 'anniversary format', label: Soho.Locale.translate('FieldPicker_anniversaryFormat_type_operator') },
    { value: 'period format', label: Soho.Locale.translate('FieldPicker_periodFormat_type_operator') },
    { value: 'using', label: Soho.Locale.translate('FieldPicker_javaFormat_type_operator') },
  ];

  public TIMESTAMP_TYPE_OPERATORS: Array<LMTypeOperatorOptionState>  = [
    { value: 'corporate date', label: Soho.Locale.translate('FieldPicker_corporateDate_type_operator') },
    { value: 'system date', label: Soho.Locale.translate('FieldPicker_systemDate_type_operator') },
    { value: 'date', label:Soho.Locale.translate('FieldPicker_date_type_operator') },
    { value: 'time', label: Soho.Locale.translate('FieldPicker_time_type_operator') },
  ];

  public DATE_FORMATTING_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'date format', label: Soho.Locale.translate('FieldPicker_dateFormat_type_operator') },
    { value: 'anniversary format', label: Soho.Locale.translate('FieldPicker_anniversaryFormat_type_operator') },
    { value: 'period format', label: Soho.Locale.translate('FieldPicker_periodFormat_type_operator') },
    { value: 'using', label: Soho.Locale.translate('FieldPicker_javaFormat_type_operator') },
  ];

  public DATE_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'anniversary', label:  Soho.Locale.translate('FieldPicker_anniversary_type_operator') },
    { value: 'period', label:  Soho.Locale.translate('FieldPicker_period_type_operator') },
    { value: 'year', label:  Soho.Locale.translate('FieldPicker_year_type_operator') },
    { value: 'year day', label:  Soho.Locale.translate('FieldPicker_yearDay_type_operator') },
    { value: 'month', label:  Soho.Locale.translate('FieldPicker_month_type_operator') },
    { value: 'month name', label:  Soho.Locale.translate('FieldPicker_monthName_type_operator') },
    { value: 'month short name', label: Soho.Locale.translate('FieldPicker_monthShortName_type_operator') },
    { value: 'day', label:  Soho.Locale.translate('FieldPicker_day_type_operator') },
    { value: 'day name', label:  Soho.Locale.translate('FieldPicker_dayName_type_operator') },
    { value: 'day short name', label:  Soho.Locale.translate('FieldPicker_dayShortName_type_operator') },
    { value: 'week', label:  Soho.Locale.translate('FieldPicker_week_type_operator') },
    { value: 'week day', label:  Soho.Locale.translate('FieldPicker_weekDay_type_operator') },
    { value: 'week year', label:  Soho.Locale.translate('FieldPicker_weekYear_type_operator') },
    { value: 'days in month', label:  Soho.Locale.translate('FieldPicker_daysInMonth_type_operator') },
    { value: 'days in year', label:  Soho.Locale.translate('FieldPicker_daysInYear_type_operator') },
    { value: 'first date in year', label: Soho.Locale.translate('FieldPicker_first_date_in_year_type_operator') },
    { value: 'first date in month', label: Soho.Locale.translate('FieldPicker_first_date_in_month_type_operator') },
    { value: 'first date in week', label: Soho.Locale.translate('FieldPicker_first_date_in_week_type_operator') },
    { value: 'last date in year', label: Soho.Locale.translate('FieldPicker_last_date_in_year_type_operator') },
    { value: 'last date in month', label: Soho.Locale.translate('FieldPicker_last_date_in_month_type_operator') },
    { value: 'last date in week', label: Soho.Locale.translate('FieldPicker_last_date_in_week_type_operator') },
  ];

  public TIME_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'hours', label: Soho.Locale.translate('FieldPicker_hours_type_operator') },
    { value: 'minutes', label: Soho.Locale.translate('FieldPicker_minutes_type_operator') },
    { value: 'seconds', label: Soho.Locale.translate('FieldPicker_seconds_type_operator') },
  ];

  public ANNIVERSARY_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'anniversary format', label: Soho.Locale.translate('FieldPicker_anniversaryFormat_type_operator') },
  ];

  public PERIOD_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'period format', label: Soho.Locale.translate('FieldPicker_periodFormat_type_operator') },
    { value: 'first date in year', label: Soho.Locale.translate('FieldPicker_first_date_in_year_type_operator') },
    { value: 'first date in month', label: Soho.Locale.translate('FieldPicker_first_date_in_month_type_operator') },
    { value: 'last date in year', label: Soho.Locale.translate('FieldPicker_last_date_in_year_type_operator') },
    { value: 'last date in month', label: Soho.Locale.translate('FieldPicker_last_date_in_month_type_operator') },

  ];

  public NUMERIC_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'as years', label: Soho.Locale.translate('FieldPicker_asYears_type_operator') },
    { value: 'as months', label: Soho.Locale.translate('FieldPicker_asMonths_type_operator')  },
    { value: 'as days', label: Soho.Locale.translate('FieldPicker_asDays_type_operator') },
  ];

  public BCD_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'decimals', label: Soho.Locale.translate('FieldPicker_decimals_type_operator') },
  ];

  public RICH_TEXT_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'plain text', label: Soho.Locale.translate('FieldPicker_plainText_type_operator') },
  ];

  public BINARY_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'mime type', label: Soho.Locale.translate('FieldPicker_mimeType_type_operator') },
    { value: 'in base64', label: Soho.Locale.translate('FieldPicker_base64_type_operator') },
  ];

  public ARRAY_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'levels', label: Soho.Locale.translate('FieldPicker_levels_type_operator') },
    { value: 'entries', label: Soho.Locale.translate('FieldPicker_entries_type_operator') },
  ];

  public COMPACT_FORMAT_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'compact format', label: Soho.Locale.translate('FieldPicker_compactFormat_type_operator') },
  ];

  public TEXT_VARIABLES_TYPE_OPERATORS: Array<LMTypeOperatorOptionState> = [
    { value: 'text', label: Soho.Locale.translate('FieldPicker_text_type_operator') },
  ];


  // TYPE OPERATORS GROUPS ============================================================================
  public TIMESTAMP_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    { label: Soho.Locale.translate('FieldPicker_formatting_group_label'), children: this.TIME_STAMP_FORMATTING_OPERATORS  },
    { label: Soho.Locale.translate('FieldPicker_timestamp_group_label'), children: this.TIMESTAMP_TYPE_OPERATORS          },
    { label: Soho.Locale.translate('FieldPicker_date_group_label'), children: this.DATE_TYPE_OPERATORS                    }
  ];

  public DATE_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_formatting_group_label'), children: this.DATE_FORMATTING_OPERATORS
    },
    {
      label: Soho.Locale.translate('FieldPicker_date_group_label'), children: this.DATE_TYPE_OPERATORS
    },
  ];

  public TIME_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_time_group_label'), children: this.TIME_TYPE_OPERATORS
    },
    {
      label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public ANNIVERSARY_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_anniversary_group_label'), children: this.ANNIVERSARY_TYPE_OPERATORS
    }
  ];

  public PERIOD_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_period_group_label'), children: this.PERIOD_TYPE_OPERATORS
    }
  ];

  public NUMERIC_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_numeric_group_label'), children: this.NUMERIC_TYPE_OPERATORS
    },
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public BCD_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_bcd_group_label'), children: this.BCD_TYPE_OPERATORS
    },
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public RICH_TEXT_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_richText_group_label'), children: this.RICH_TEXT_TYPE_OPERATORS
    },
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public BINARY_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_binaryField_group_label'), children: this.BINARY_TYPE_OPERATORS
    },
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public ARRAY_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_array_group_label'), children: this.ARRAY_TYPE_OPERATORS
    },
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public COMPACT_FORMAT_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_groupField_group_label'), children: this.COMPACT_FORMAT_TYPE_OPERATORS
    },
    {
      label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public TEXT_VARIABLES_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_textVariables_group_label'), children: this.TEXT_VARIABLES_TYPE_OPERATORS
    },
  ];

  public STRING_TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    {
      label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.STRING_TYPE_OPERATORS
    },
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public TYPE_OPERATORS_GROUP: Array<LMTypeOperatorOptionState> = [
    // {
    //   label: Soho.Locale.translate('FieldPicker_other_group_label'), children: this.OTHER_OPERATORS
    // },
    {
      label: Soho.Locale.translate('FieldPicker_fieldInfo_group_label'), children: this.FIELD_INFO_TYPE_OPERATORS
    },
  ];

  public FORMAT_STYLES: Array<LMFormatStyleOptionState> = [
    { value: 'short', label: Soho.Locale.translate('FieldPicker_shortFormat_operator') },
    { value: 'medium', label: Soho.Locale.translate('FieldPicker_mediumFormat_operator') },
    { value: 'long', label: Soho.Locale.translate('FieldPicker_longFormat_operator') },
  ];

  constructor(private http: HttpClient,
    private sohoModalDialogService: SohoModalDialogService,
    private lmLoggerService: LMLoggerService,
    private lmContextService: LMContextService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormatUtilService: LMFormatUtilService,
    private zone: NgZone,
  ) {
    // no-op
  }

  public getBusinessClassForRelatedField(dataview: LMDataView, fieldName: string, resourceId: string, url: string): Observable<BusClassNameResponse> {
    const params = {
      fieldName,
      requestType: 'GetRelatedFieldBusinessClass',
      dataView: dataview
    };
    const templateUrl = urltemplate.parse(url);
    const urlCompiled = templateUrl.expand({ resourceId });
    return <Observable<BusClassNameResponse>>this.http.post(urlCompiled, params).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  public openFieldHelpDialog(uiFieldSpec: UIFieldSpec) {
    let fieldPickerHelpComponent: LMFieldPickerHelpComponent;
    const formName = 'fieldPickerHelp';

    // Create modal button
    const buttons = [];
    buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ formName, LMDialogButtonKey.CLOSE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
          click: () => {
            modalRef.close(true);
          },
        }
    );


    const title = uiFieldSpec.label;

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-field-picker-help-modal`
    } ];

    const sohoModalOptions: SohoModalOptions = {
      fullsize: 'responsive',
      attributes
    };

    const modalRef = this.sohoModalDialogService.modal(LMFieldPickerHelpComponent, undefined, sohoModalOptions)
        .title(title)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((dialogComponent: LMFieldPickerHelpComponent) => {
          fieldPickerHelpComponent = dialogComponent;
          fieldPickerHelpComponent.uiFieldSpec = uiFieldSpec;
        });
  }

  public openFieldOperatorDialog(options: LMFieldPickerOperatorOptions): Observable<LMFieldPickerSelectResponse> {
    let targetComponent: LMFieldPickerOperatorComponent;
    const returnQuery = new Subject<LMFieldPickerSelectResponse>();
    const formName = 'fieldPickerOperator';
    options.operatorInfo = this.validateOperatorInfo(options.auditData, options.uiFieldSpec, options.operatorInfo);

    const closeModalWith = (result) => {
      modalRef.close(true);
      this.zone.run(() => {
        returnQuery.next(result);
        returnQuery.complete();
      });
    };

    // Create modal button
    const buttons = [];
    buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ options.businessClass, formName, LMDialogButtonKey.CLOSE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
          click: () => {
            closeModalWith(undefined);
          },
        }
    );

    if (options.operatorInfo) {
      buttons.push(
          {
            id: this.lmUniqueIdService.uniqueId([options.businessClass, formName, LMDialogButtonKey.CLEAR]),
            text: Soho.Locale.translate('FieldPicker_RemoveOperatorLink'),
            click: () => {
              const response = {
                fieldName: this.buildFieldNameWithOperatorInfo(targetComponent.uiFieldSpec, undefined, targetComponent.view),
                uiFieldSpec: targetComponent.uiFieldSpec,
                operatorInfo: undefined
              };
              closeModalWith(response);
            },
          }
      );
    }

    buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ options.businessClass, formName, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          click: () => {
              const response = {
                fieldName: this.buildFieldNameWithOperatorInfo(targetComponent.uiFieldSpec, targetComponent.operatorInfo, targetComponent.view),
                uiFieldSpec: targetComponent.uiFieldSpec,
                operatorInfo: targetComponent.operatorInfo
              };
              closeModalWith(response);
          },
          isDefault: true,
        }
    );

    const title = options.operatorInfo ? Soho.Locale.translate('FieldPicker_EditOperatorLink') : Soho.Locale.translate('FieldPicker_AddOperatorLink');

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-field-picker-operator-modal`
    } ];

    const sohoModalOptions: SohoModalOptions = {
      fullsize: 'responsive',
      attributes
    };

    const modalRef = this.sohoModalDialogService.modal(LMFieldPickerOperatorComponent, undefined, sohoModalOptions)
        .title(title)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((dialogComponent: LMFieldPickerOperatorComponent) => {
          targetComponent = dialogComponent;
          targetComponent.uiFieldSpec = options.uiFieldSpec;
          targetComponent.operatorInfo = options.operatorInfo;
          targetComponent.view = options.view;
          targetComponent.auditData = options.auditData;
          targetComponent.validPeriods = options.validPeriods;
        })
        .beforeClose(() => {
          // if ref.dialogResult is undefined, the escape key was used
          if (!modalRef.dialogResult) {
            closeModalWith(undefined);
            return true;
          }
        });

    return returnQuery.asObservable();
  }

  public openFieldPickerDialog(options: LMFieldPickerSelectOptions, fieldLookUpUrl: string,  placeHolder: ViewContainerRef, selectedFieldSpec: UIFieldSpec = null, operatorInfo: LMOperatorInfo = null): Observable<LMFieldPickerSelectResponse> {
    let fieldPickerComponent: LMFieldPickerComponent;
    const returnQuery = new Subject<LMFieldPickerSelectResponse>();
    const formName = 'fieldPicker';

    const closeModalWith = (result) => {
      modalRef.close(true);
      this.zone.run(() => {
        returnQuery.next(result);
        returnQuery.complete();
      });
    };

    // Create modal button
    const buttons: SohoModalButton[] = [];
    buttons.push(
      {
        /**
         * Using a 'Close' label here (instead of 'Cancel') appends the entire id
         * to the automationId. Adding cssClass of btn-cancel seems to fix this issue.
         */
        id: this.lmUniqueIdService.uniqueId([ options.businessClass, formName, LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        cssClass: 'btn-cancel',
        click: () => {
          closeModalWith(undefined);
        },
      }
    );

    if (options.enableOperatorBuilder) {
      const showOperator = operatorInfo; //this.showOperatorOnInitialize(options, selectedFieldSpec);
      buttons.push(
        {
          id: LMFieldPickerService.ADD_OPERATOR_BUTTON_ID,
          text: showOperator ? Soho.Locale.translate('FieldPicker_SkipOperatorLabel')  : Soho.Locale.translate('FieldPicker_AddOperatorLabel'),
          click: () => {
            const newLabel = fieldPickerComponent.showingOperatorBuilder ? Soho.Locale.translate('FieldPicker_AddOperatorLabel') : Soho.Locale.translate('FieldPicker_SkipOperatorLabel');
            const sohoButtonOptions: Array<SohoButtonOptions> = modalRef.buttonsetAPI?.settings?.buttons || [];

            for (let i = 1; i < sohoButtonOptions.length; i++) {
              if (sohoButtonOptions[i].id === LMFieldPickerService.ADD_OPERATOR_BUTTON_ID) {
                sohoButtonOptions[i].text = newLabel;
                modalRef.buttonsetAPI.at(i).updated(sohoButtonOptions[i]);
                break;
              }
            }
            fieldPickerComponent.toggleOperator();
          },
        }
      );
    }

    buttons.push(
      {
        id: this.lmUniqueIdService.uniqueId([ options.businessClass, formName, LMDialogButtonKey.OK ]),
        text: Soho.Locale.translate(LMDialogButtonKey.OK),
        click: () => {
          fieldPickerComponent.onFieldSpecSelected(() => {
            const response = {
              fieldName: this.buildFieldNameWithOperatorInfo(fieldPickerComponent.selectedRow, fieldPickerComponent.fieldOperatorInfo, fieldPickerComponent.options.view),
              uiFieldSpec: fieldPickerComponent.selectedRow,
              operatorInfo: fieldPickerComponent.fieldOperatorInfo
            };
            closeModalWith(response);
          });
        },
        isDefault: true,
      }
    );

    let title: string;
    if (options.view === 'RelatedLinks') {
      title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldPicker_RelatedLink_Dialog_Title'), options.businessClass);
    } else if (options.view === 'Measures') {
      title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldPicker_Measure_Dialog_Title'), options.businessClass);
    } else if (options.view === 'SimpleConditions') {
      title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldPicker_SimpleCondition_Dialog_Title'), options.businessClass);
    } else if (options.view === 'Dimensions' || options.view === 'DimensionsWithAttributes' ){
      title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldPicker_Dimension_Dialog_Title'), options.businessClass);
    } else {
      title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldPicker_Field_Dialog_Title'), options.businessClass);
    }

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-field-picker-modal`
    } ];

    const sohoModalOptions: SohoModalOptions = {
      fullsize: 'responsive',
      attributes
    };

    const modalRef = this.sohoModalDialogService.modal(LMFieldPickerComponent, placeHolder, sohoModalOptions)
      .title(title)
      .buttons(buttons)
      .isAlert(false)
      .open()
      .apply((dialogComponent: LMFieldPickerComponent) => {
        fieldPickerComponent = dialogComponent;
        fieldPickerComponent.lookupUrl = fieldLookUpUrl;
        fieldPickerComponent.options = options;
        fieldPickerComponent.selectedFieldSpec = selectedFieldSpec;
        fieldPickerComponent.operatorInfo = operatorInfo;

        fieldPickerComponent.uiFieldSpecSelectionEvent.subscribe((event: LMFieldPickerRowSelectionEvent) => {
          if (event && event.row) {
            const response = {
              fieldName: this.buildFieldNameWithOperatorInfo(event.row, fieldPickerComponent.fieldOperatorInfo, fieldPickerComponent.options.view),
              uiFieldSpec: event.row
            };
            closeModalWith(response);
          }
        });

        if (options.enableOperatorBuilder) {
          fieldPickerComponent.rowSelectedEvent.subscribe((event: LMFieldPickerRowSelectionEvent) => {
            if (event && event.row) {
              const sohoButtonOptions: Array<SohoButtonOptions> = modalRef.buttonsetAPI?.settings?.buttons || [];

              if (options.view === 'Fields') {
                let disabled =  !this.supportsFieldOperator(event.row);

                if (options.includeAuditFields) {
                  disabled = false; // always available for audit lists so use can choose old value.
                }

                for (let i = 1; i < sohoButtonOptions.length; i++) {
                  if (sohoButtonOptions[i].id === LMFieldPickerService.ADD_OPERATOR_BUTTON_ID) {
                    modalRef.buttonsetAPI.at(i).disabled = disabled;
                  }
                }
              }
            }
          });
        }
      })
      .beforeClose(() => {
        // if ref.dialogResult is undefined, the escape key was used
        if (!modalRef.dialogResult) {
          closeModalWith(undefined);
          return true;
        }
      });

    return returnQuery.asObservable();
  }

  public getFieldSpecData(params: LMFieldPickerRequest, url: string, businessClass: string): Observable<LMFieldPickerResponse> {
    const templateUrl = urltemplate.parse(url);
    const urlCompiled = templateUrl.expand({ resourceId: businessClass });
    return this._makeHttpCall(urlCompiled, params);
  }

  public getCancellableFieldSpecData(params: LMFieldPickerRequest, url: string, businessClass: string): Observable<LMFieldPickerResponse> {
    const templateUrl = urltemplate.parse(url);
    const urlCompiled = templateUrl.expand({ resourceId: businessClass });

    return new Observable<LMFieldPickerResponse>(observer => {
      const cancelSubject$ = new Subject<boolean>();
      cancelSubject$.subscribe(cancelled => {
        observer.next({ status: 'CANCELED' });
        observer.complete();
      });

      const cancellableNotifier = new LMFieldPickerSearchNotifier(observer, cancelSubject$, 10000);
      cancellableNotifier.start();
      this._makeCancellableHttpCall(cancelSubject$, urlCompiled, params).subscribe((response: LMListPagingResponse) => {
        cancellableNotifier.finish();
        observer.next(response);
        observer.complete();
      });
    });
  }

  private _makeHttpCall(url: string, params: LMFieldPickerRequest): Observable<LMFieldPickerResponse> {
    return <Observable<LMFieldPickerResponse>>this.http.post(url, params).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  private _makeCancellableHttpCall(cancelSubject$: Subject<boolean>, url: string, params: LMFieldPickerRequest): Observable<LMFieldPickerResponse> {
    return <Observable<LMFieldPickerResponse>>this.http.post(url, params).pipe(takeUntil(cancelSubject$),
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  public isOTMField(uiFieldSpec: UIFieldSpec): boolean {

    if (uiFieldSpec && uiFieldSpec.parentFieldTypes) {
      const parentFieldTypes = uiFieldSpec.parentFieldTypes.split('.');
      return parentFieldTypes.length > 0 && parentFieldTypes.includes('OTM_RELATION');
    }

    return false;
  }

  public getUIFieldSpecSearchName(uiFieldSpec: UIFieldSpec, searchFrom?: UIFieldSpec): string {
    if (uiFieldSpec.depth > 1) {
      return uiFieldSpec.fieldName;
    } else {
      return this.getUIFieldSpecFullName(uiFieldSpec, searchFrom);
    }
  }

  public getUIFieldSpecFullName(uiFieldSpec: UIFieldSpec, searchFrom?: UIFieldSpec): string {
    if (uiFieldSpec.parent) {
      let childName;
      const agentParent = uiFieldSpec.parent.endsWith('agent');

      if (agentParent) {
        childName = `${uiFieldSpec.parent}${uiFieldSpec.fieldName}`;
      } else {
        childName = `${uiFieldSpec.parent}.${uiFieldSpec.fieldName}`;
      }

      if (searchFrom) {
        const searchFromName = this.getUIFieldSpecFullName(searchFrom);
        const suffix = agentParent ? '' : '.';
        return childName.replace(searchFromName + suffix, '');
      } else {
        return childName;
      }
    }
    return uiFieldSpec.fieldName;
  }

  public buildFieldNameWithOperatorInfo(uiFieldSpec: UIFieldSpec, operatorInfo: LMOperatorInfo, view: LMFieldPickerView): string {
    if (!uiFieldSpec) {
      return '';
    }

    const fieldName = this.getUIFieldSpecFullName(uiFieldSpec);

    if (!operatorInfo || view === 'RelatedLinks') {
      return fieldName;
    }

    if (view === 'Measures') {
      return this.buildPeriodOperatorLPL(fieldName, operatorInfo.periodOperatorInfo);
    }

    // Field and Type Operators for Fields and SimpleConditions
    let fieldOperator = operatorInfo?.fieldOperator ? operatorInfo.fieldOperator : '';

    if (this.isOTMField(uiFieldSpec) && !fieldOperator) {
      fieldOperator = 'first';
    }

    const typeOperator = this.buildTypeOperatorLPL(uiFieldSpec, operatorInfo);
    const array = [ fieldOperator, fieldName, typeOperator ];
    return array.join(' ').trim();
  }

  private buildPeriodOperatorLPL(measure: string, info: LMPeriodOperatorInfo): string {
    const yearOperator = this.buildYearOperator(info.yearInfo);
    const periodOperator = this.buildPeriodOperator(info.periodInfo);
    const lpl = `${yearOperator} ${periodOperator}`.trim();
    return `${measure} ${lpl}`.trim();
  }

  private buildYearOperator(info: LMYearPeriodInfo): string {
    if (!info) {
      return '';
    }

    if (info.yearOperator) {
      const year = info.yearOperator && info.year? info.year : '';
      return `${info.yearOperator} ${year}`.trim();
    } else {
      return '';
    }
  }

  private buildPeriodOperator(info: LMPeriodInfo): string {
    if (!info) {
      return '';
    }

    const priorNext = info.priorNext ? info.priorNext : '';
    const period = info.periodOperator ? info.periodOperator : 'period';
    const thruNumber = info.to ? info.to : '';
    const fromNumber = info.from ? info.from : '';
    const baseLPL = `${priorNext} ${period} ${fromNumber}`.trim();

    if (info.category === 'periodRange') {
      if (!thruNumber || !fromNumber) {
        return `${period} ${fromNumber}`.trim();
      } else {
        return `${period} ${fromNumber} thru ${thruNumber}`.trim();
      }
    } else if (info.category === 'beginning balance') {
      return `${baseLPL} beginning balance`;
    } else if (info.category === 'beginning ytd balance') {
      return `${baseLPL} beginning ytd balance`;
    } else if (info.category === 'ending balance') {
      return `${baseLPL} ending balance`;
    } else if (info.category === 'ending ytd balance') {
      return `${baseLPL} ending ytd balance`;
    } else if (info.category === 'period') {
      return baseLPL;
    }

    return '';
  }

  private buildTypeOperatorLPL(uiFieldSpec: UIFieldSpec, operatorInfo: LMOperatorInfo): string {
    if (operatorInfo?.typeOperatorInfo) {
      const typeOperator = operatorInfo.typeOperatorInfo.typeOperator;

      switch (typeOperator) {
        case undefined:
          return '';
        case 'period format':
          return operatorInfo.typeOperatorInfo.formatStyle + ' period';
        case 'anniversary format':
          return operatorInfo.typeOperatorInfo.formatStyle + ' anniversary ' + this.buildWithDay(uiFieldSpec, operatorInfo.typeOperatorInfo);
        case 'date format':
          return operatorInfo.typeOperatorInfo.formatStyle + ' date ' + this.buildWithDay(uiFieldSpec, operatorInfo.typeOperatorInfo);
        case 'timestamp format':
          return operatorInfo.typeOperatorInfo.formatStyle + ' timestamp ' + this.buildWithDay(uiFieldSpec, operatorInfo.typeOperatorInfo);
        case 'using':
          return typeOperator + ' "' + operatorInfo.typeOperatorInfo?.javaFormat + '"';
        case 'split':
          return typeOperator + ' on "' + operatorInfo.typeOperatorInfo.splitOn + '"';
        default:
          return typeOperator;
      }

      return '';
    }
  }

  private buildWithDay(uiFieldSpec: UIFieldSpec, info: LMTypeOperatorInfo): string {
    const sb: Array<string> = new Array();

    if (info.withDayName) {
      if (info.withShortDayName) {
        sb.push('with day short name');
      } else {
        sb.push('with day name');
      }
    }

    if (uiFieldSpec.lmrkFieldType === AppConstants.TIMESTAMP) {
      if (info.usingTimeZone) {
        sb.push(info.usingTimeZone);
      }
      if (info.showTimeZone) {
        sb.push('show time zone');
      }

      if (info.hideSeconds) {
        sb.push('hide seconds');
      }
    }

    return sb.join(' ');
  }

  public buildFormatExample(operatorInfo: LMOperatorInfo): string {

    if (!operatorInfo) {
      return '';
    }

    if (operatorInfo.typeOperatorInfo) {
      const typeOperator = operatorInfo.typeOperatorInfo.typeOperator;

      switch (typeOperator) {
        case 'period format':
          return this.buildPeriodFormatExample(operatorInfo.typeOperatorInfo);
        case 'anniversary format':
          return this.buildAnniversaryFormatExample(operatorInfo.typeOperatorInfo);
        case 'date format':
          return this.buildDateFormatExample(operatorInfo.typeOperatorInfo);
        case 'timestamp format':
          return this.buildTimestampFormatExample(operatorInfo.typeOperatorInfo);
        default:
          return '';
      }
    }
  }

  private buildTimestampFormatExample(typeOperatorInfo: LMTypeOperatorInfo): string {
    const date = new Date();

    const options: Intl.DateTimeFormatOptions = {};
    switch (typeOperatorInfo.formatStyle) {
      case 'short':
        options.year      = '2-digit';
        options.month     = 'numeric';
        options.day       = '2-digit';
        options.hour      = '2-digit';
        options.minute    = '2-digit';

        if (!typeOperatorInfo.hideSeconds) {
          options.second    = '2-digit';
        }
        break;
      case 'medium':
        options.year      = 'numeric';
        options.month     = 'short';
        options.day       = 'numeric';
        options.hour      = '2-digit';
        options.minute    = '2-digit';

        if (!typeOperatorInfo.hideSeconds) {
          options.second    = '2-digit';
        }
        break;
      case 'long':
        options.year      = 'numeric';
        options.month     = 'long';
        options.day       = 'numeric';
        options.hour      = '2-digit';
        options.minute    = '2-digit';

        if (!typeOperatorInfo.hideSeconds) {
          options.second    = '2-digit';
        }
        break;
      default:
        return '';
    }

    if (typeOperatorInfo.withDayName) {
      if (typeOperatorInfo.withShortDayName) {
        options.weekday = 'short';
      } else {
        options.weekday = 'long';
      }
    }

    if (typeOperatorInfo.showTimeZone) {
      options.timeZoneName = 'short';
      if (typeOperatorInfo.usingTimeZone === 'using corporate time zone') {
        options.timeZoneName = 'shortGeneric';
        options.timeZone = 'GMT';
      } else if (typeOperatorInfo.usingTimeZone === '') {
        options.timeZoneName = 'shortGeneric';
        options.timeZone = 'GMT';
      }
    }

    return date.toLocaleString(Soho.Locale.currentLocale.name, options);
  }

  private buildDateFormatExample(typeOperatorInfo: LMTypeOperatorInfo): string {
    const date = new Date();
    switch (typeOperatorInfo.formatStyle) {
      case 'short':
        if (typeOperatorInfo.withDayName) {
          if (typeOperatorInfo.withShortDayName) {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { year: '2-digit', month: 'numeric', day:'2-digit', weekday: 'short' });
          } else {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { year: '2-digit', month: 'numeric', day:'2-digit', weekday: 'long' });
          }
        } else {
          return date.toLocaleString(Soho.Locale.currentLocale.name, { year: '2-digit', month: 'numeric', day:'numeric' });
        }
        break;
      case 'medium':
        if (typeOperatorInfo.withDayName) {
          if (typeOperatorInfo.withShortDayName) {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month: 'short', day:'2-digit', weekday: 'short' });
          } else {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month: 'short', day:'2-digit', weekday: 'long' });
          }
        } else {
          return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month: 'short', day:'numeric' });
        }
        break;
      case 'long':
        if (typeOperatorInfo.withDayName) {
          if (typeOperatorInfo.withShortDayName) {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month: 'long', day:'2-digit', weekday: 'short' });
          } else {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month: 'long', day:'2-digit', weekday: 'long' });
          }
        } else {
          return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month: 'long', day:'numeric' });
        }
        break;
      default:
        return '';
    }
  }

  private buildAnniversaryFormatExample(typeOperatorInfo: LMTypeOperatorInfo): string {
    const date = new Date();

    switch (typeOperatorInfo.formatStyle) {
      case 'short':
        if (typeOperatorInfo.withDayName) {
          if (typeOperatorInfo.withShortDayName) {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'numeric', day:'2-digit', weekday: 'short' });
          } else {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'numeric', day:'2-digit', weekday: 'long' });
          }
        } else {
          return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'numeric', day:'numeric' });
        }
        break;
      case 'medium':
        if (typeOperatorInfo.withDayName) {
          if (typeOperatorInfo.withShortDayName) {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'short', day:'2-digit', weekday: 'short' });
          } else {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'short', day:'2-digit', weekday: 'long' });
          }
        } else {
          return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'short', day:'numeric' });
        }
        break;
      case 'long':
        if (typeOperatorInfo.withDayName) {
          if (typeOperatorInfo.withShortDayName) {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'long', day:'2-digit', weekday: 'short' });
          } else {
            return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'long', day:'2-digit', weekday: 'long' });
          }
        } else {
          return date.toLocaleString(Soho.Locale.currentLocale.name, { month: 'long', day:'numeric' });
        }
        break;
      default:
        return '';
    }
  }

  private buildPeriodFormatExample(typeOperatorInfo: LMTypeOperatorInfo): string {
    const date = new Date();

    switch (typeOperatorInfo.formatStyle) {
      case 'short':
        return date.toLocaleString(Soho.Locale.currentLocale.name, { year: '2-digit', month:'numeric' });
        return '';
      case 'medium':
        return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month:'short' });
        return '';
      case 'long':
        return date.toLocaleString(Soho.Locale.currentLocale.name, { year: 'numeric', month:'long' });
      default:
        return '';
    }
  }

  public supportsFieldOperator(uiFieldSpec: UIFieldSpec): boolean {

    if (!uiFieldSpec) {
      return false;
    }

    if (this.isOTMField(uiFieldSpec)) {
      return true;
    }

    if (uiFieldSpec.lmrkFieldType === AppConstants.YYYYMMDD
    || uiFieldSpec.lmrkFieldType === AppConstants.TIMESTAMP
    || uiFieldSpec.lmrkFieldType === AppConstants.MMDD
    || uiFieldSpec.lmrkFieldType === AppConstants.YYYYMM
    || uiFieldSpec.lmrkFieldType === AppConstants.ALPHA
    || uiFieldSpec.lmrkFieldType === AppConstants.ALPHARIGHT
    || uiFieldSpec.lmrkFieldType === AppConstants.ALPHAUPPER
    || uiFieldSpec.lmrkFieldType === AppConstants.ALPHALOWER
    || uiFieldSpec.lmrkFieldType === AppConstants.TEXT) {
      return true;
    } else {
      return false;
    }

    // return !(!uiFieldSpec
    //     || uiFieldSpec.fieldType === 'STATE'
    //     || uiFieldSpec.fieldType === 'OTM_RELATION'
    //     || uiFieldSpec.fieldType === 'OTO_RELATION'
    //     || (uiFieldSpec.fieldType === 'GROUP' && !uiFieldSpec.isCompactFormat));
  }

  public boldQuery(str, query): string {

    if (!str || !query) {
      return '';
    }
    const n = str.toUpperCase();
    const q = query.toUpperCase();
    const x = n.indexOf(q);
    if (!q || x === -1) {
      return str; // bail early
    }
    const l = q.length;
    return str.substr(0, x) + '<b>' + str.substr(x, l) + '</b>' + str.substr(x + l);
  }

  public createFieldPickerView(lplFieldInfo: LMLPLFieldInfo): LMFieldPickerView {
    if (lplFieldInfo.mode === 'RelatedLink') {
      return 'RelatedLinks';
    } else if (lplFieldInfo.dimensionsOrMeasuresOnly) {
      if (lplFieldInfo.dimensionsOrMeasuresOnly === 'measures') {
        return 'Measures';
      } else {
        return (lplFieldInfo.withAttributes) ? 'DimensionsWithAttributes' : 'Dimensions';
      }
    } else {
      return 'Fields';
    }
  }

  public includesAuditFields(lplFieldInfo: LMLPLFieldInfo): boolean {
    if (lplFieldInfo.mode === 'RelatedLink') {
      return false;
    } else {
      return !lplFieldInfo.dimensionsOrMeasuresOnly;
    }
  }

  public fieldPickerOperatorsEnabled(lplFieldInfo: LMLPLFieldInfo): boolean {
    if (lplFieldInfo.dimensionsOrMeasuresOnly &&  lplFieldInfo.dimensionsOrMeasuresOnly === 'measures'){
      return true;
    } else {
      return false;
    }
  }

  public defaultPeriodFrom(info: LMPeriodOperatorInfo) {
    if (info.periodInfo?.priorNext === 'prior' || info.periodInfo?.priorNext === 'next') {
      info.periodInfo.from = 1;
    } else {
      switch (info.periodInfo.periodOperator) {
        case 'period':
        case 'week':
        case 'month':
        case 'quarter':
          info.periodInfo.from = info.periodInfo.category === 'periodRange' ? 1 : undefined;
          break;
        case 'year':
          const date = new Date();
          info.periodInfo.from = date.getFullYear(); // info.periodInfo.category === 'periodRange' ? undefined : date.getFullYear();
          break;
        default:
          info.periodInfo.from = info.periodInfo.category === 'periodRange' ? 1 : undefined;

      }
    }
  }

  public defaultPeriodTo(info: LMPeriodOperatorInfo) {
    if (info.periodInfo?.priorNext === 'prior' || info.periodInfo?.priorNext === 'next') {
      info.periodInfo.to = 1;
    } else {
      switch (info.periodInfo.periodOperator) {
        case 'period':
        case 'week':
        case 'month':
        case 'quarter':
          info.periodInfo.to = info.periodInfo.category === 'periodRange' ? 1 : undefined;
          break;
        case 'year':
          const date = new Date();
          info.periodInfo.to = date.getFullYear(); //info.periodInfo.category === 'periodRange' ? undefined : date.getFullYear();
          break;
        default:
          info.periodInfo.to = info.periodInfo.category === 'periodRange' ? 1 : undefined;
      }
    }
  }

  public buildFieldOperatorOptions(isAuditData: boolean, uiFieldSpec: UIFieldSpec): Array<LMFieldOperatorOptionState> {
    if (!uiFieldSpec) {
      return [];
    }

    if (uiFieldSpec.lmrkFieldType) {
      if (isAuditData) {
        return [ { value: 'old', label: Soho.Locale.translate('FieldPicker_old_field_operator') } ];
      }

      if (this.isOTMField(uiFieldSpec)) {
        if (uiFieldSpec.lmrkFieldType === AppConstants.BCD) {
          return  this.OTM_BCD_FIELD_OPERATORS_GROUP;
        } else {
          return this.OTM_FIELD_OPERATORS_GROUP;
        }
      }
    }

    return [];
  }

  public buildTypeOperatorOptions(uiFieldSpec: UIFieldSpec): Array<LMTypeOperatorOptionState> {
    if (!uiFieldSpec) {
      return [];
    }

    if (uiFieldSpec.lmrkFieldType) {
      switch (uiFieldSpec.lmrkFieldType) {
        case AppConstants.YYYYMMDD:
          return this.DATE_TYPE_OPERATORS_GROUP;
        case AppConstants.TIMESTAMP:
          return this.TIMESTAMP_TYPE_OPERATORS_GROUP;
        case AppConstants.MMDD:
          return this.ANNIVERSARY_TYPE_OPERATORS_GROUP;
          break;
        case AppConstants.YYYYMM:
          return this.PERIOD_TYPE_OPERATORS_GROUP;
        case AppConstants.ALPHA:
        case AppConstants.ALPHALOWER:
        case AppConstants.ALPHAUPPER:
        case AppConstants.ALPHARIGHT:
        case AppConstants.TEXT:
          return this.STRING_TYPE_OPERATORS;
      }
    }

    return [];
  }

  public buildFieldStyleOptions(uiFieldSpec: UIFieldSpec): Array<LMFormatStyleOptionState> {
    if (!uiFieldSpec) {
      return [];
    }

    if (uiFieldSpec.lmrkFieldType === AppConstants.TIMESTAMP
        || uiFieldSpec.lmrkFieldType === AppConstants.YYYYMMDD
        || uiFieldSpec.lmrkFieldType === AppConstants.YYYYMM
        || uiFieldSpec.lmrkFieldType === AppConstants.MMDD) {
      return  this.FORMAT_STYLES;
    }

    return [];
  }

  public blankOperatorInfo(): LMOperatorInfo {
    const info: LMOperatorInfo = {
      periodOperatorInfo: {
        yearInfo: { yearOperator: undefined, year: undefined },
        periodInfo: { category: 'period', priorNext: undefined, periodOperator: 'period', to: undefined, from: undefined }
      },
      fieldOperator: undefined,
      typeOperatorInfo: {
        typeOperator: undefined,
        asOfDate: undefined,
        splitOn: ',',
        useFullName: undefined,
        useShortName: false,
        javaFormat: undefined,
        formatStyle: 'short',
        withShortDayName: false,
        withDayName: false,
        hideSeconds: false,
        showTimeZone: false,
        usingTimeZone: 'using user time zone'
      } };
    return info;
  }
  public validateOperatorInfo(isAuditData: boolean, uiFieldSpec: UIFieldSpec, operatorInfo: LMOperatorInfo): LMOperatorInfo {

    if (operatorInfo?.fieldOperator) {
      const fieldOperators:Array<LMFieldOperatorOptionState> = this.buildFieldOperatorOptions(isAuditData, uiFieldSpec);
      if (fieldOperators.length === 0) {
        operatorInfo.fieldOperator = undefined;
      }
    }


    return operatorInfo;
  }

  public showOperatorOnInitialize(options:LMFieldPickerSelectOptions, uiFieldSpec: UIFieldSpec) :  boolean {

    if (options.view === 'Measures') {

      return uiFieldSpec !== null;
    }

    return options.enableOperatorBuilder && uiFieldSpec !== null && this.supportsFieldOperator(uiFieldSpec) ? true : false;
  }
}

export class LMFieldPickerSearchNotifier {
  private clock: Observable<number>;
  private finished = false;
  private observer: Observer<LMFieldPickerResponse>;
  private cancelSubject$: Subject<boolean>;
  private interval: number;

  constructor(observer: Observer<LMFieldPickerResponse>, cancelSubject$: Subject<boolean>, interval: number) {
    this.observer = observer;
    this.cancelSubject$ = cancelSubject$;
    this.interval = interval;
  }

  public start() {
    this.observer.next({ status: 'STARTED', cancelableNotifier: this.cancelSubject$ });
    this.setTimer();
  }

  public finish() {
    this.finished = true;
  }

  private setTimer() {
    this.clock = timer(this.interval);
    this.clock.subscribe(x => {
      if (this.finished) {
        return;
      }

      this.observer.next({ status: 'WAITING' });
      this.setTimer();
    });
  }
}

